/**
 * BMI 计算工具函数
 * 使用中国成人BMI标准 (WS/T 428-2013)
 */

import type { BMICategory, Gender, ActivityLevel, GoalType } from '../types';

/**
 * BMI分类标准（中国成人）
 * - 偏瘦: < 18.5
 * - 正常: 18.5 - 23.9
 * - 超重: 24.0 - 27.9
 * - 肥胖: ≥ 28.0
 */
export const BMI_STANDARDS = {
  UNDERWEIGHT: { max: 18.5, label: '偏瘦', color: 'blue' },
  NORMAL: { min: 18.5, max: 23.9, label: '正常', color: 'green' },
  OVERWEIGHT: { min: 24.0, max: 27.9, label: '超重', color: 'orange' },
  OBESE: { min: 28.0, label: '肥胖', color: 'red' }
} as const;

/**
 * 活动水平系数
 */
export const ACTIVITY_MULTIPLIERS: Record<ActivityLevel, number> = {
  sedentary: 1.2,      // 久坐不动
  light: 1.375,        // 轻度活动
  moderate: 1.55,      // 中度活动
  active: 1.725,       // 高度活动
  very_active: 1.9     // 非常活跃
};

/**
 * 计算BMI值
 * @param height - 身高 (cm)
 * @param weight - 体重 (kg)
 * @returns BMI值，保留1位小数
 */
export function calculateBMI(height: number, weight: number): number {
  if (height <= 0 || weight <= 0) {
    return 0;
  }
  const heightInMeters = height / 100;
  const bmi = weight / (heightInMeters * heightInMeters);
  return Math.round(bmi * 10) / 10;
}

/**
 * 根据BMI值获取分类
 * @param bmi - BMI值
 * @returns BMI分类
 */
export function getBMICategory(bmi: number): BMICategory {
  if (bmi < 18.5) {
    return 'underweight';
  } else if (bmi < 24.0) {
    return 'normal';
  } else if (bmi < 28.0) {
    return 'overweight';
  } else {
    return 'obese';
  }
}

/**
 * 获取BMI分类的中文标签
 * @param category - BMI分类
 * @returns 中文标签
 */
export function getBMICategoryLabel(category: BMICategory): string {
  const labels: Record<BMICategory, string> = {
    underweight: '偏瘦',
    normal: '正常',
    overweight: '超重',
    obese: '肥胖'
  };
  return labels[category];
}

/**
 * 获取BMI分类的颜色类名（用于Tailwind CSS）
 * @param category - BMI分类
 * @returns 颜色类名字符串
 */
export function getBMICategoryColorClass(category: BMICategory): string {
  const colors: Record<BMICategory, string> = {
    underweight: 'text-blue-600 bg-blue-50 border-blue-200',
    normal: 'text-green-600 bg-green-50 border-green-200',
    overweight: 'text-orange-600 bg-orange-50 border-orange-200',
    obese: 'text-red-600 bg-red-50 border-red-200'
  };
  return colors[category];
}

/**
 * 获取BMI分类的健康建议
 * @param category - BMI分类
 * @returns 健康建议文本
 */
export function getBMIHealthAdvice(category: BMICategory): string {
  const advice: Record<BMICategory, string> = {
    underweight: '您的体重偏轻，建议适当增加营养摄入，保持均衡饮食和适量运动。',
    normal: '您的体重在正常范围内，请继续保持健康的生活方式！',
    overweight: '您的体重超标，建议控制饮食热量，增加运动量，保持健康体重。',
    obese: '您的体重已达到肥胖标准，建议咨询专业医生或营养师，制定科学的减重计划。'
  };
  return advice[category];
}

/**
 * 计算年龄
 * @param birthdate - 出生日期 (YYYY-MM-DD)
 * @returns 年龄（岁）
 */
export function calculateAge(birthdate: string): number {
  if (!birthdate) {
    return 25; // 默认年龄
  }
  
  const birth = new Date(birthdate);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  
  return age > 0 ? age : 0;
}

/**
 * 计算BMR（基础代谢率）
 * 使用Mifflin-St Jeor公式
 * @param gender - 性别
 * @param weight - 体重 (kg)
 * @param height - 身高 (cm)
 * @param age - 年龄
 * @returns BMR值 (kcal)
 */
export function calculateBMR(
  gender: Gender,
  weight: number,
  height: number,
  age: number
): number {
  if (weight <= 0 || height <= 0 || age <= 0) {
    return 0;
  }
  
  // Mifflin-St Jeor公式
  let bmr = (10 * weight) + (6.25 * height) - (5 * age);
  
  if (gender === 'male') {
    bmr += 5;
  } else if (gender === 'female') {
    bmr -= 161;
  } else {
    // 未指定性别时使用中性调整值
    bmr -= 78;
  }
  
  return Math.round(bmr);
}

/**
 * 计算TDEE（每日总能量消耗）
 * @param bmr - 基础代谢率
 * @param activityLevel - 活动水平
 * @returns TDEE值 (kcal)
 */
export function calculateTDEE(
  bmr: number,
  activityLevel: ActivityLevel
): number {
  if (bmr <= 0) {
    return 0;
  }
  
  const multiplier = ACTIVITY_MULTIPLIERS[activityLevel] || 1.2;
  return Math.round(bmr * multiplier);
}

/**
 * 根据目标计算推荐卡路里摄入
 * @param tdee - TDEE值
 * @param goalType - 目标类型
 * @param gender - 性别（用于计算最低安全摄入量）
 * @returns 推荐卡路里摄入
 */
export function calculateRecommendedCalories(
  tdee: number,
  goalType: GoalType | string,
  gender?: Gender
): number {
  if (tdee <= 0) {
    return 0;
  }
  
  let recommended = tdee;
  
  switch (goalType) {
    case 'lose_weight':
      recommended = tdee - 500;
      break;
    case 'gain_weight':
      recommended = tdee + 500;
      break;
    case 'muscle_building':
      recommended = tdee + 300;
      break;
    case 'maintain':
    default:
      recommended = tdee;
      break;
  }
  
  // 确保不低于最低安全摄入量
  const minCalories = gender === 'female' ? 1200 : 1500;
  return Math.max(recommended, minCalories);
}

/**
 * 生成宏量营养素目标
 * @param calories - 目标卡路里
 * @param weight - 体重 (kg)
 * @param goalType - 目标类型
 * @returns 宏量营养素目标
 */
export function calculateMacroTargets(
  calories: number,
  weight: number,
  goalType?: GoalType
): {
  protein: number;
  fat: number;
  carbs: number;
  fiber: number;
} {
  if (calories <= 0 || weight <= 0) {
    return { protein: 0, fat: 0, carbs: 0, fiber: 0 };
  }
  
  // 蛋白质: 根据目标调整
  let proteinGrams: number;
  if (goalType === 'muscle_building') {
    // 增肌: 1.6-2.2g/kg，取中间值
    proteinGrams = Math.round(weight * 1.9);
  } else {
    // 其他: 0.8-1.2g/kg，取中间值
    proteinGrams = Math.round(weight * 1.0);
  }
  
  // 蛋白质热量
  const proteinCalories = proteinGrams * 4;
  
  // 脂肪: 20-30%，取25%
  const fatCalories = calories * 0.25;
  const fatGrams = Math.round(fatCalories / 9);
  
  // 碳水: 剩余热量
  const carbsCalories = calories - proteinCalories - fatCalories;
  const carbsGrams = Math.round(carbsCalories / 4);
  
  // 纤维: 固定推荐量
  const fiberGrams = 25;
  
  return {
    protein: proteinGrams,
    fat: fatGrams,
    carbs: carbsGrams,
    fiber: fiberGrams
  };
}

/**
 * 完整的身体数据分析
 * @param params - 身体数据参数
 * @returns 完整的分析结果
 */
export function analyzeBodyData(params: {
  gender: Gender;
  birthdate: string;
  height: number;
  weight: number;
  activityLevel: ActivityLevel;
  goalType: GoalType;
}): {
  bmi: number;
  bmiCategory: BMICategory;
  age: number;
  bmr: number;
  tdee: number;
  recommendedCalories: number;
  macros: {
    protein: number;
    fat: number;
    carbs: number;
    fiber: number;
  };
} {
  const { gender, birthdate, height, weight, activityLevel, goalType } = params;
  
  // 计算年龄
  const age = calculateAge(birthdate);
  
  // 计算BMI
  const bmi = calculateBMI(height, weight);
  const bmiCategory = getBMICategory(bmi);
  
  // 计算BMR和TDEE
  const bmr = calculateBMR(gender, weight, height, age);
  const tdee = calculateTDEE(bmr, activityLevel);
  
  // 计算推荐摄入
  const recommendedCalories = calculateRecommendedCalories(tdee, goalType, gender);
  
  // 计算宏量营养素
  const macros = calculateMacroTargets(recommendedCalories, weight, goalType);
  
  return {
    bmi,
    bmiCategory,
    age,
    bmr,
    tdee,
    recommendedCalories,
    macros
  };
}
